import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {validHref, deprecatedProp} from '@homebase/common';
import BaseButton from '../BaseButton';
import './Button.$BRAND.css.js';

const noop = () => {};

// For using an icon on the left side of a button.
export const IconLeft = ({children}) => (
  <span className="Button-iconLeft">{children}</span>
);

// @ignore-prop-docs
IconLeft.propTypes = {
  // The Icon that is within the Button to the left of the button text content
  children: PropTypes.node.isRequired,
};

// For using an icon on the right side of a button.
export const IconRight = ({children}) => (
  <span className="Button-iconRight">{children}</span>
);

// @ignore-prop-docs
IconRight.propTypes = {
  // The Icon that is within the Button to the right of the button text content
  children: PropTypes.node.isRequired,
};

const Button = (props) => {
  const {
    children,
    disabled,
    fullWidth,
    href,
    openInNewWindow,
    hasIcon,
    iconOnly,
    type,
    onClick,
    onMouseEnter,
    onMouseLeave,
    onBlur,
    size,
    variation,
    dataEventName,
    dataGroupName,
    dataClickLocation,
    isB2B,
    isPlainText,
    testIds,
    noreferrer,
    is,
    'data-hb-id': dataHbId = 'Button',
    ...rest
  } = props;

  const buttonClasses = cx('Button', `Button--${variation}`, {
    [`Button--${size}`]: size,
    'Button--hasIcon': hasIcon,
    'Button--hiddenText': hasIcon && iconOnly,
    'Button--fullWidth': fullWidth,
    'Button--plainText': isPlainText,
    'js-track-event': dataEventName || dataGroupName,
    'Button--b2b': isB2B,
  });

  return (
    <BaseButton
      {...rest}
      testIds={testIds}
      openInNewWindow={openInNewWindow}
      noreferrer={noreferrer}
      className={buttonClasses}
      data-event-name={dataEventName}
      data-group-name={dataGroupName}
      data-click-location={dataClickLocation}
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onBlur={onBlur}
      type={type}
      href={href}
      data-hb-id={dataHbId}
      is={is}
    >
      <span className="Button-content">{children}</span>
    </BaseButton>
  );
};

Button.defaultProps = {
  disabled: false,
  // @ignore-prop-docs
  dataEventName: null,
  // @ignore-prop-docs
  dataGroupName: null,
  // @ignore-prop-docs
  dataClickLocation: null,
  hasIcon: false,
  children: null,
  iconOnly: false,
  href: null,
  // @ignore-prop-docs
  size: null,
  type: 'button',
  variation: 'primary',
  onClick: noop,
  onMouseEnter: noop,
  onMouseLeave: noop,
  onBlur: noop,
  isB2B: false,
  isPlainText: false,
  fullWidth: false,
  openInNewWindow: false,
  testIds: {},
  noreferrer: true,
  is: null,
};

Button.propTypes = {
  // @ignore-prop-docs
  // Custom Homebase id attribute
  'data-hb-id': PropTypes.string,
  // If true, adds style adjustments. Set this to true if the button content has an icon and text.
  hasIcon: deprecatedProp(
    'Prefer using IconLeft or IconRight components which can be imported from the button component.',
    [false]
  ),
  // If true adds style adjustments. Set this to true if the button content has an icon and NO text.
  iconOnly: deprecatedProp(
    'Prefer using circle button for buttons which only have an Icon.',
    [false]
  ),
  // If true the button will expand to the full width of it's container.
  fullWidth: PropTypes.bool,
  // Handler to be called when the component is clicked.
  onClick: PropTypes.func,
  // Handler to be called onMouseEnter.
  onMouseEnter: PropTypes.func,
  // Handler to be called onMouseLeave.
  onMouseLeave: PropTypes.func,
  // Handler to be called when the component loses focus.
  onBlur: PropTypes.func,
  // If true the button is disabled.
  disabled: PropTypes.bool,
  // The event name used for click tracking
  // @ignore-prop-docs
  dataEventName: deprecatedProp(
    'Please wrap this component with one of the tracking components instead `https://docs.csnzoo.com/javascript/patterns/tracking/`.'
  ),
  // The group name used for click tracking
  // @ignore-prop-docs
  dataGroupName: deprecatedProp(
    'Please wrap this component with one of the tracking components instead `https://docs.csnzoo.com/javascript/patterns/tracking/`.'
  ),
  // The click location used for click tracking
  // @ignore-prop-docs
  dataClickLocation: deprecatedProp(
    'Please wrap this component with one of the tracking components instead `https://docs.csnzoo.com/javascript/patterns/tracking/`.'
  ),
  // Alternate sizes for the button.
  // @ignore-prop-docs
  size: deprecatedProp(
    'Only a single button size is supported. Please remove this prop.'
  ),
  // The type of button element - use `submit` if button submits a `<form>`.
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  // The variation to use - this prop is what determines the color scheme of the button.
  variation: PropTypes.oneOf([
    'primary',
    'secondary',
    'alternate',
    'transaction',
    'transactionAlt',
    'sale',
    'veil',
    'pill',
    'group',
  ]),
  // The content of the button.
  children: PropTypes.node,
  // If true link buttons open in a new window/tab.
  openInNewWindow: PropTypes.bool,
  // Provides a url for buttons being used as a link.
  href: validHref,
  // If true the B2B theme is applied.
  isB2B: PropTypes.bool,
  // If true the standard button styling is removed and the component appears as normal text.
  isPlainText: PropTypes.bool,
  // Optional Enzyme and Codeception testing identifiers
  testIds: PropTypes.shape({
    enzyme: PropTypes.string,
    codeception: PropTypes.string,
  }),
  // Optional prop used in combination with the openInNewWindow prop, to add noreferrer to the rel attribute
  // Should be set to false for internal links only due to [no support](https://caniuse.com/#search=noopener) for `noopener` on IE11/Edge.
  // Without `noreferrer` there is a security risk for external links: [[1]](https://mathiasbynens.github.io/rel-noopener/), [[2]](https://github.com/yannickcr/eslint-plugin-react/blob/master/docs/rules/jsx-no-target-blank.md)
  noreferrer: PropTypes.bool,
  // The HTML element the component will render as.
  is: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.func,
    PropTypes.string,
  ]),
};

Button.IconLeft = IconLeft;
Button.IconRight = IconRight;

export default Button;
